import React from 'react';
import { Box, Chip, LinearProgress, Paper, Typography } from '@mui/material';
import { Database, BarChart3, Link2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDay } from '../api';
import { useHftStore } from '../store';

const SEGMENT_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  nse_eq: { label: 'NSE Equity', color: '#3b82f6', icon: <BarChart3 size={20} /> },
  nse_fut: { label: 'NSE Futures', color: '#f59e0b', icon: <BarChart3 size={20} /> },
  nse_idx: { label: 'NSE Indices', color: '#10b981', icon: <BarChart3 size={20} /> },
  nse_opt: { label: 'NSE Options', color: '#ef4444', icon: <Link2 size={20} /> },
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1e6) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1e9) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${(bytes / 1e9).toFixed(2)} GB`;
}

function formatRows(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1e6) return `${(n / 1e3).toFixed(1)}K`;
  if (n < 1e9) return `${(n / 1e6).toFixed(1)}M`;
  return `${(n / 1e9).toFixed(2)}B`;
}

export default function DayOverview() {
  const { current, push } = useHftStore();
  const date = current.nav.date;
  const { data, isLoading } = useDay(date);

  if (isLoading) return <LinearProgress sx={{ borderRadius: 1 }} />;
  if (!data) return <Typography color="text.secondary">No data for this date.</Typography>;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>
        {date}
      </Typography>
      <Box display="flex" gap={2} flexWrap="wrap">
        {data.segments.map((seg, i) => {
          const meta = SEGMENT_META[seg.segment] || { label: seg.segment, color: '#94a3b8', icon: <Database size={20} /> };
          const layers = seg.layers || {};
          const hasChain = !!layers['gold_chain'];

          return (
            <motion.div
              key={seg.segment}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              style={{ flex: '1 1 240px', minWidth: 220 }}
            >
              <Paper
                elevation={0}
                onClick={() => push('segment', { segment: seg.segment })}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(30,41,59,0.5)' : 'rgba(248,250,252,0.8)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: meta.color,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 20px ${meta.color}22`,
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <Box sx={{ color: meta.color }}>{meta.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {meta.label}
                  </Typography>
                </Box>

                {/* Layer badges */}
                <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                  {['bronze', 'silver', 'gold'].map((layer) => {
                    // API returns gold_candles/gold_chain, not plain "gold"
                    const present = layer === 'gold'
                      ? !!(layers['gold_candles'] || layers['gold_chain'])
                      : !!layers[layer];
                    return (
                      <Chip
                        key={layer}
                        label={layer.charAt(0).toUpperCase() + layer.slice(1)}
                        size="small"
                        color={present ? (layer === 'gold' ? 'warning' : layer === 'silver' ? 'info' : 'default') : 'default'}
                        variant={present ? 'filled' : 'outlined'}
                        sx={{ fontSize: 11, opacity: present ? 1 : 0.4 }}
                      />
                    );
                  })}
                </Box>

                {/* Options Chain — prominent button for nse_opt */}
                {hasChain && (
                  <Chip
                    label="View Options Chain (IV + Greeks)"
                    size="small"
                    color="secondary"
                    variant="filled"
                    sx={{ fontSize: 11, cursor: 'pointer', mb: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      push('chain', {
                        segment: seg.segment,
                        underlying: 'NIFTY',
                      });
                    }}
                  />
                )}

                {/* Stats */}
                {layers['silver'] && (
                  <Typography variant="caption" color="text.secondary">
                    {formatRows((layers['silver'] as any).row_count || 0)} rows
                    {' · '}
                    {formatBytes((layers['silver'] as any).size_bytes || 0)}
                  </Typography>
                )}
              </Paper>
            </motion.div>
          );
        })}
      </Box>
    </Box>
  );
}
